import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("nl-NL");
}

function statusLabel(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved") return "Goedgekeurd";
  if (key === "rejected") return "Afgewezen";
  return "In behandeling";
}

function statusClass(status) {
  const key = String(status || "").trim().toLowerCase();
  if (key === "approved") return "bg-emerald-100 text-emerald-700";
  if (key === "rejected") return "bg-red-100 text-red-700";
  return "bg-amber-100 text-amber-700";
}

export default function MonteurLeaveRequestsPage() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    fromDate: "",
    toDate: "",
    reason: "",
  });

  const pendingCount = useMemo(
    () => items.filter((item) => String(item?.status || "").trim().toLowerCase() === "pending").length,
    [items]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/leave-requests/mine");
      setItems(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Verlofaanvragen laden mislukt");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function submit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.fromDate || !form.toDate) {
      setError("Vul begin- en einddatum in.");
      return;
    }

    if (new Date(form.toDate) < new Date(form.fromDate)) {
      setError("Einddatum mag niet voor begindatum liggen.");
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/leave-requests", {
        fromDate: form.fromDate,
        toDate: form.toDate,
        reason: String(form.reason || "").trim(),
      });
      setForm({ fromDate: "", toDate: "", reason: "" });
      setSuccess("Verlofaanvraag verstuurd.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Verlofaanvraag versturen mislukt");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Verlof</h2>
            <p className="text-sm text-gray-500">Vraag verlof aan en volg de status.</p>
          </div>
          <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
            Openstaand: {pendingCount}
          </span>
        </div>
      </section>

      {error ? (
        <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </section>
      ) : null}

      {success ? (
        <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
          {success}
        </section>
      ) : null}

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Nieuwe aanvraag</h3>
        <form onSubmit={submit} className="grid gap-3 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm text-gray-600">Van</span>
            <input
              type="date"
              value={form.fromDate}
              onChange={(e) => setForm((prev) => ({ ...prev, fromDate: e.target.value }))}
              className="w-full rounded border p-2"
              required
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm text-gray-600">Tot</span>
            <input
              type="date"
              value={form.toDate}
              onChange={(e) => setForm((prev) => ({ ...prev, toDate: e.target.value }))}
              className="w-full rounded border p-2"
              required
            />
          </label>
          <label className="block md:col-span-2">
            <span className="mb-1 block text-sm text-gray-600">Toelichting (optioneel)</span>
            <textarea
              value={form.reason}
              onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
              rows={3}
              className="w-full rounded border p-2"
              placeholder="Bijv. vakantie, familie-afspraak, etc."
            />
          </label>
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? "Versturen..." : "Verlof aanvragen"}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Mijn aanvragen</h3>

        {loading ? <p className="text-sm text-gray-500">Laden...</p> : null}

        {!loading && items.length === 0 ? (
          <p className="text-sm text-gray-500">Nog geen aanvragen.</p>
        ) : null}

        {!loading && items.length > 0 ? (
          <div className="space-y-2">
            {items.map((item) => (
              <article key={item._id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {formatDate(item?.fromDate)} t/m {formatDate(item?.toDate)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Aangevraagd op {formatDate(item?.createdAt)}
                    </p>
                    {item?.reason ? <p className="mt-1 text-sm text-gray-700">{item.reason}</p> : null}
                    {item?.reviewNote ? (
                      <p className="mt-1 text-xs text-gray-600">Opmerking reviewer: {item.reviewNote}</p>
                    ) : null}
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusClass(item?.status)}`}>
                    {statusLabel(item?.status)}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : null}
      </section>
    </div>
  );
}
